import { useToast } from '@chakra-ui/react';
import { CargoType } from '@pelicargo/types';
import { cancelReasonOptions } from '@pelicargo/utils';
import { useMemo } from 'react';

import {
  AirlineBookingSection,
  AirlineBookingValues,
  BookingType,
  useAirlineBookingForm,
} from '../../hooks/emails/useAirlineBookingForm';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { useEmail } from '../../hooks/useEmail';
import { ContactTags } from '../../utils/constants';
import { sendEmailsToValidProviders } from '../../utils/email';
import { EmailDrawer } from '../Emails/EmailDrawer';
import { EmailDrawerLeftPanel } from '../Emails/EmailDrawerLeftPanel';
import { EmailDrawerRightPanel } from '../Emails/EmailDrawerRightPanel';
import { SimpleSharedEmailForm } from '../Emails/SimpleSharedEmailForm';
import { useRequestDrawersContext } from '../RequestDrawers';
import { SimpleAirlineRecipientForm } from '../SimpleAirlineRecipientForm';
import { SimpleFieldArray } from '../SimpleFieldArray';
import { SimpleForm } from '../SimpleForm';
import { SimpleSelect } from '../SimpleSelect';

export const BookingCancelDrawer = () => {
  const toast = useToast();
  const { request, quotes } = useCurrentRequest();
  const { sendAirlineBookingCancelEmail } = useEmail();

  const { methods } = useAirlineBookingForm(request, quotes, BookingType.Cancel);

  const { getIsModalOpen, closeModal } = useRequestDrawersContext();
  const isOpen = getIsModalOpen('BookingCancel');
  const onClose = () => closeModal('BookingCancel');

  const sendSingleEmail = async (
    provider: AirlineBookingValues['providers'][0],
    data: Omit<AirlineBookingValues, 'providers'>,
  ) => {
    if (!provider?.isExpanded || !provider?.recipients?.length) return;

    const { id: requestId } = request;
    const { quoteId, recipients } = provider;
    const [toContact, ...ccContacts] = recipients;
    const { senderContactId, ...overrides } = data;

    await sendAirlineBookingCancelEmail({
      requestId,
      quoteId,
      senderContactId,
      toContact,
      ccContacts,
      overrides,
    });
  };

  const onValid = async (data: AirlineBookingSection) => {
    try {
      const { airlines, trucks, warehouses } = data;

      // Send emails for all provider types if they have data
      const promises = [];

      if (airlines?.providers?.length) {
        const { providers, ...airlineData } = airlines;
        promises.push(sendEmailsToValidProviders(airlineData, providers as any, sendSingleEmail));
      }

      if (trucks?.providers?.length) {
        const { providers, ...truckData } = trucks;
        promises.push(sendEmailsToValidProviders(truckData, providers as any, sendSingleEmail));
      }

      if (warehouses?.providers?.length) {
        const { providers, ...warehouseData } = warehouses;
        promises.push(sendEmailsToValidProviders(warehouseData, providers as any, sendSingleEmail));
      }

      await Promise.all(promises);
      toast({ title: 'Success', description: 'Emails sent successfully', status: 'success' });
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  const onError = (error: any) => {
    toast({ title: 'Error', description: error?.message, status: 'error' });
  };

  const handleSubmit = methods.handleSubmit(onValid, onError);

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const defaultTags = useMemo(() => {
    const tags = [ContactTags.Bookings];
    if (request?.cargo_type === CargoType.Perishable) {
      tags.push(ContactTags.TempControl);
    }

    return tags;
  }, [request?.cargo_type]);

  return (
    <SimpleForm {...methods} submitOnBlur={false}>
      <form onSubmit={handleSubmit}>
        <EmailDrawer isOpen={isOpen} onClose={handleClose}>
          <EmailDrawerLeftPanel
            title="Select Airline"
            description="Select the airline and specify the contacts you want to send the email to"
          >
            <SimpleFieldArray
              name="airlines.providers"
              canAdd={false}
              canRemove={false}
              ignoreStyles
              form={({ prefix }) => (
                <SimpleAirlineRecipientForm prefix={prefix} defaultTags={defaultTags} searchPattern="AIRLINE" />
              )}
            />
          </EmailDrawerLeftPanel>
          <EmailDrawerRightPanel title="Cancel Booking">
            <SimpleSelect name="airlines.cancelReason" label="Cancel Reason" options={cancelReasonOptions} />
            <SimpleSharedEmailForm name="airlines" onSubmit={handleSubmit} onCancel={handleClose} />
          </EmailDrawerRightPanel>
        </EmailDrawer>
      </form>
    </SimpleForm>
  );
};
